import React from 'react'
import styled from 'styled-components'

const TransactionId = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #1a1a1a;
`

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
`

const ReasonBadge = styled.span<{ $reason: string }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 9px;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;

  ${({ $reason }) => {
    switch ($reason?.toLowerCase()) {
      case 'achat':
        return 'background: rgba(2, 148, 223, 0.1); color: #0294df;'
      case 'vente':
        return 'background: rgba(0, 179, 65, 0.1); color: #00B341;'
      case 'donation':
        return 'background: rgba(255, 149, 0, 0.1); color: #FF9500;'
      case 'succession':
        return 'background: rgba(142, 142, 147, 0.1); color: #8E8E93;'
      case 'echange':
        return 'background: rgba(128, 90, 213, 0.1); color: #805AD5;'
      case 'partage':
        return 'background: rgba(255, 59, 48, 0.1); color: #FF3B30;'
      default:
        return 'background: rgba(142, 142, 147, 0.1); color: #6B6B6B;'
    }
  }}
`

const Price = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #0294df;
`

const InlineDetails = styled.div`
  margin-top: 6px;
  font-size: 10px;
  color: #666;
  line-height: 1.5;
`

const Separator = styled.span`
  color: #ccc;
  margin: 0 3px;
`

const Address = styled.div`
  margin-top: 4px;
  font-size: 9px;
  color: #bbb;
`

const formatPrice = (price?: number | string): string => {
  if (!price) return ''
  const numPrice = typeof price === 'string' ? parseFloat(price) : price
  if (isNaN(numPrice)) return ''
  return new Intl.NumberFormat('de-CH', {
    style: 'currency',
    currency: 'CHF',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numPrice)
}

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return ''
  try {
    return new Date(dateStr).toLocaleDateString('de-CH')
  } catch {
    return ''
  }
}

interface TransactionMapTooltipProps {
  properties: Record<string, any>
}

export const TransactionMapTooltip: React.FC<TransactionMapTooltipProps> = ({ properties }) => {
  const reason = properties?.transaction_step_name || properties?.reason
  const price = formatPrice(properties?.transaction_total_price || properties?.price)
  const date = formatDate(properties?.transaction_date || properties?.date)

  const location = properties?.address || properties?.postal_codes_names || ''

  // Build inline detail parts
  const details: string[] = []
  if (date) details.push(date)
  if (properties?.transaction_area) {
    details.push(`${properties.transaction_area} m\u00B2`)
  }

  const buyersCount = properties?.buyers_count || properties?.buyers_nb || 0
  const sellersCount = properties?.sellers_count || properties?.sellers_nb || 0
  if (buyersCount || sellersCount) {
    details.push(`${buyersCount}B / ${sellersCount}S`)
  }

  return (
    <>
      <TransactionId>{properties?.official_id || `#${properties?.id || ''}`}</TransactionId>

      <BadgeRow>
        {reason && <ReasonBadge $reason={reason}>{reason}</ReasonBadge>}
        {price && <Price>{price}</Price>}
      </BadgeRow>

      {details.length > 0 && (
        <InlineDetails>
          {details.map((detail, i) => (
            <React.Fragment key={i}>
              {i > 0 && <Separator>&middot;</Separator>}
              <span style={{ fontWeight: 500, color: '#1a1a1a' }}>{detail}</span>
            </React.Fragment>
          ))}
        </InlineDetails>
      )}

      {location && <Address>{location}</Address>}
    </>
  )
}
